import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import { svgElementToString, svgToPngBlob } from "./download";
import { COLORS, TAGLINE, PILLARS } from "./tokens";

/**
 * Build a premium multi-page designer brief PDF.
 * A4 portrait · 210 × 297 mm.
 * Page 1  Cover
 * Page 2  Brand overview & pillars
 * Page 3  Palette
 * Page 4  Typography
 * Page 5  Logo system — what exists & what to refine
 * Page 6  Banners & platform sizes
 * Page 7  Deliverables, timeline & how to contact
 */
export async function generateDesignerBrief({ mainLogoEl, walkLogoEl, microEl, sigEl, pillarEl }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const W = 210, H = 297;
  const M = 22;

  // Brief owner contact — pre-filled
  const CONTACT = {
    from: "Rona Wellness",
    email: "robinahallgaier@gmail.com",
    timeline: "Flexible — open to discuss",
    budget: "$150 USD (flexible, open to discuss)",
  };

  // helpers
  const setFill = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    doc.setFillColor(r, g, b);
  };
  const setStroke = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    doc.setDrawColor(r, g, b);
  };
  const setText = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    doc.setTextColor(r, g, b);
  };

  // Convert SVG el → PNG data URL for embedding
  const svgToDataURL = async (el, w, h) => {
    if (!el) return null;
    const str = svgElementToString(el);
    const blob = await svgToPngBlob(str, w, h);
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const mainLogoUrl = await svgToDataURL(mainLogoEl, 900, 900);
  const walkLogoUrl = await svgToDataURL(walkLogoEl, 1000, 525);
  const microUrl = await svgToDataURL(microEl, 400, 400);
  const sigUrl = await svgToDataURL(sigEl, 900, 500);
  const pillarUrl = await svgToDataURL(pillarEl, 260, 325);

  // ---------- shared header / footer ----------
  const drawPageChrome = (pageNo, total) => {
    // top rule + eyebrow
    setStroke(COLORS.gold);
    doc.setLineWidth(0.3);
    doc.line(M, 18, W - M, 18);
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    setText(COLORS.goldDeep);
    doc.text("RONA WELLNESS  ·  DESIGNER BRIEF  ·  2026", M, 14);
    doc.text(`${pageNo} / ${total}`, W - M, 14, { align: "right" });
    // bottom rule
    doc.line(M, H - 18, W - M, H - 18);
    doc.setFontSize(8);
    setText(COLORS.inkSoft);
    doc.text(TAGLINE, M, H - 12);
    doc.text(PILLARS, W - M, H - 12, { align: "right" });
  };

  const TOTAL_PAGES = 7;

  // ============ PAGE 1 — COVER ============
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");
  // subtle gold frame
  setStroke(COLORS.gold);
  doc.setLineWidth(0.4);
  doc.rect(M - 6, M - 6, W - (M - 6) * 2, H - (M - 6) * 2);

  if (mainLogoUrl) {
    doc.addImage(mainLogoUrl, "PNG", W / 2 - 40, 50, 80, 80);
  }

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  setText(COLORS.goldDeep);
  doc.text("BRAND IDENTITY · DESIGNER BRIEF", W / 2, 150, { align: "center", charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(42);
  setText(COLORS.ink);
  doc.text("Rona Wellness", W / 2, 175, { align: "center" });

  doc.setFont("times", "italic");
  doc.setFontSize(14);
  setText(COLORS.goldDeep);
  doc.text(`"${TAGLINE}"`, W / 2, 188, { align: "center" });

  // pillar line
  setStroke(COLORS.gold);
  doc.setLineWidth(0.2);
  doc.line(W / 2 - 30, 200, W / 2 + 30, 200);

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  setText(COLORS.inkSoft);
  doc.text(PILLARS, W / 2, 208, { align: "center", charSpace: 3 });

  // bottom cover block
  doc.setFontSize(9);
  setText(COLORS.inkSoft);
  doc.text("Prepared for:", W / 2, 240, { align: "center" });
  doc.setFont("times", "italic");
  doc.setFontSize(16);
  setText(COLORS.ink);
  doc.text("Ritz  ·  Designsbyritz", W / 2, 250, { align: "center" });

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.inkSoft);
  doc.text("A first-draft visual system has been built. This brief outlines", W / 2, 258, { align: "center" });
  doc.text("the concept, palette, and what we'd love you to refine.", W / 2, 263, { align: "center" });

  // Sender block at the very bottom of the cover
  setStroke(COLORS.gold);
  doc.setLineWidth(0.2);
  doc.line(W / 2 - 30, 273, W / 2 + 30, 273);
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("FROM", W / 2, 279, { align: "center", charSpace: 2 });
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  setText(COLORS.ink);
  doc.text(CONTACT.from, W / 2, 286, { align: "center" });
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  setText(COLORS.inkSoft);
  doc.text(CONTACT.email, W / 2, 292, { align: "center" });

  drawPageChrome(1, TOTAL_PAGES);

  // ============ PAGE 2 — BRAND OVERVIEW ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("01  ·  BRAND OVERVIEW", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(32);
  setText(COLORS.ink);
  doc.text("Calm. Grounded.", M, 58);
  doc.text("Quietly feminine.", M, 72);

  doc.setFont("times", "normal");
  doc.setFontSize(11);
  setText(COLORS.ink);
  const overview = [
    "Rona Wellness was born from a quiet belief — that healing does not always begin",
    "with dramatic change. It often begins with one gentle step.",
    "",
    "Rona Wellness is a LUXURY LIFESTYLE BRAND — walking for wellness. Not simply a",
    "product brand, but a lifestyle movement grounded in mindful movement, nature,",
    "walking, reflection, connection, and inner peace.",
    "",
    "MISSION: To inspire healing, mindful movement, inner peace, hope, and connection.",
    "VISION:  A globally recognized wellness lifestyle brand for intentional living.",
    "VALUES:  Hope · Wellness · Connection · Authenticity · Growth · Grounding · Community.",
    "",
    "The concept — The Rona Walk — sits at the heart of the system: morning walks,",
    "peaceful movement, walking as spiritual self-care, and quiet routines that",
    "restore the soul.",
  ];
  let y = 88;
  overview.forEach((line) => { doc.text(line, M, y); y += 6.2; });

  // Pillars block
  y += 6;
  setStroke(COLORS.gold);
  doc.setLineWidth(0.25);
  doc.line(M, y, M + 30, y);
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("THE THREE PILLARS", M, y + 6, { charSpace: 2 });

  const pillars = [
    { word: "Faith", desc: "Spiritual self-care woven into everyday routine." },
    { word: "Strength", desc: "Quiet resilience. Movement as a gentle ritual." },
    { word: "Hope", desc: "The horizon ahead. A path forward, one step at a time." },
  ];
  y += 14;
  pillars.forEach((p) => {
    doc.setFont("times", "italic");
    doc.setFontSize(16);
    setText(COLORS.ink);
    doc.text(p.word, M, y);
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    setText(COLORS.inkSoft);
    doc.text(p.desc, M + 36, y - 1);
    y += 10;
  });

  // Brand feeling keywords
  y += 8;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("BRAND FEELING", M, y, { charSpace: 2 });
  y += 8;
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  setText(COLORS.ink);
  doc.text("calm · warm · grounded · feminine · spiritual · minimal", M, y);

  // Audience
  y += 16;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("AUDIENCE", M, y, { charSpace: 2 });
  y += 6;
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  setText(COLORS.ink);
  doc.text("Special heart: the working woman who feels overwhelmed.", M, y);
  y += 6;
  doc.text("Wider community: friends, family, and everyone who walks alongside her.", M, y);
  y += 6;
  doc.text("Inclusive events (Friends & Family walks, seasonal gatherings) are core.", M, y);

  drawPageChrome(2, TOTAL_PAGES);

  // ============ PAGE 3 — PALETTE ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("02  ·  PALETTE", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(32);
  setText(COLORS.ink);
  doc.text("Calm earth,", M, 58);
  doc.text("quiet sky.", M, 72);

  doc.setFont("times", "normal");
  doc.setFontSize(10.5);
  setText(COLORS.inkSoft);
  doc.text("Warm neutrals grounded by sage and soft brown, lifted by gold and a whisper of sun.", M, 86);

  const swatches = [
    { name: "Sage Green", hex: COLORS.sage, role: "Tree, leaves, secondary grounding" },
    { name: "Gold", hex: COLORS.gold, role: "Accent, outer ring, stems, signature" },
    { name: "Dusty Blue", hex: COLORS.dustyBlue, role: "Main logo background, flower petals" },
    { name: "Soft Brown", hex: COLORS.brown, role: "Roots, path, earthy warmth" },
    { name: "Cream", hex: COLORS.cream, role: "Primary background, off-white surfaces" },
    { name: "Soft Yellow", hex: COLORS.yellow, role: "Sun glow only — no harsh rays" },
  ];

  const swX = M;
  const swY = 100;
  const swW = (W - M * 2 - 10) / 3;
  const swH = 58;
  swatches.forEach((s, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = swX + col * (swW + 5);
    const y2 = swY + row * (swH + 14);
    setFill(s.hex);
    doc.rect(x, y2, swW, swH, "F");
    // hex label inside
    doc.setFont("times", "normal");
    doc.setFontSize(9);
    const isDark = ["Sage Green", "Gold", "Dusty Blue", "Soft Brown"].includes(s.name);
    setText(isDark ? "#FFFFFF" : COLORS.ink);
    doc.text(s.hex.toUpperCase(), x + 3, y2 + swH - 3);
    // name & role below
    doc.setFont("times", "italic");
    doc.setFontSize(12);
    setText(COLORS.ink);
    doc.text(s.name, x, y2 + swH + 6);
    doc.setFont("times", "normal");
    doc.setFontSize(8.5);
    setText(COLORS.inkSoft);
    doc.text(s.role, x, y2 + swH + 11);
  });

  drawPageChrome(3, TOTAL_PAGES);

  // ============ PAGE 4 — TYPOGRAPHY ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("03  ·  TYPOGRAPHY", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(32);
  setText(COLORS.ink);
  doc.text("Refined.", M, 58);
  doc.text("Editorial. Feminine.", M, 72);

  const typeBlocks = [
    {
      role: "DISPLAY",
      name: "Cormorant Garamond",
      sample: "Rona Wellness",
      sampleSize: 36,
      note: "For the brand name, hero headlines and large print moments.",
    },
    {
      role: "ACCENT",
      name: "Marcellus",
      sample: "FAITH · STRENGTH · HOPE",
      sampleSize: 18,
      note: "Small caps, taglines, labels, banner details. Generous letter-spacing.",
    },
    {
      role: "SCRIPT",
      name: "Sacramento",
      sample: "where movement meets peace",
      sampleSize: 24,
      note: "Occasional handwritten touches — packaging, thank-you cards, tags.",
    },
  ];
  let ty = 100;
  typeBlocks.forEach((t) => {
    // divider
    setStroke(COLORS.gold);
    doc.setLineWidth(0.2);
    doc.line(M, ty, M + 20, ty);
    doc.setFont("times", "normal");
    doc.setFontSize(8);
    setText(COLORS.goldDeep);
    doc.text(`${t.role} · ${t.name}`, M, ty + 5, { charSpace: 2 });
    doc.setFont("times", "italic");
    doc.setFontSize(t.sampleSize);
    setText(COLORS.ink);
    doc.text(t.sample, M, ty + 18);
    doc.setFont("times", "normal");
    doc.setFontSize(9.5);
    setText(COLORS.inkSoft);
    doc.text(t.note, M, ty + 26);
    ty += 44;
  });

  drawPageChrome(4, TOTAL_PAGES);

  // ============ PAGE 5 — LOGO SYSTEM ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("04  ·  LOGO SYSTEM — CURRENT DRAFTS", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(28);
  setText(COLORS.ink);
  doc.text("Five marks. One voice.", M, 58);

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  setText(COLORS.inkSoft);
  doc.text("First-draft SVG masters have been built. Each is ready for your refinement.", M, 68);

  // 2-column grid: logo image + description
  const items = [
    {
      url: mainLogoUrl, w: 40, h: 40,
      title: "Main Logo", brief:
        "Tree · roots · flowing path · soft sun glow inside dusty-blue disc.\nREFINE: more organic canopy texture; slightly softer trunk; keep the\npath flowing forward naturally (NO RW shape in the roots)."
    },
    {
      url: walkLogoUrl, w: 48, h: 25,
      title: "The Rona Walk", brief:
        "The letter 'L' in WALK becomes a walking woman; heart below tagline.\nREFINE: elegant serif wordmark; feminine figure with softer flow;\nall in gold."
    },
    {
      url: microUrl, w: 22, h: 22,
      title: "Micro Icon — RW-Feather", brief:
        "A slender vertical feather framing an elegant flowing RW monogram.\nBarbs fan gently outward from the central quill. Used for favicon,\nsocial avatars, embroidery labels & premium woven tags.\nREFINE: hand-drawn feather barbs; refined script RW; balance the quill."
    },
    {
      url: sigUrl, w: 44, h: 24,
      title: "RW Signature", brief:
        "Handwritten connected RW. Must digitize cleanly for embroidery.\nREFINE: genuine calligraphy hand; variable stroke weight;\ngold / black / white versions."
    },
    {
      url: pillarUrl, w: 18, h: 22,
      title: "Pillar Symbol", brief:
        "Flower (dusty blue) + leaf (sage) + stem (gold) — sits under the\nbrand name. Represents Faith · Strength · Hope.\nREFINE: make the flower slightly more botanical and less geometric."
    },
  ];

  let iy = 82;
  for (const it of items) {
    if (it.url) doc.addImage(it.url, "PNG", M, iy, it.w, it.h);
    const textX = M + 52;
    doc.setFont("times", "italic");
    doc.setFontSize(14);
    setText(COLORS.ink);
    doc.text(it.title, textX, iy + 5);
    doc.setFont("times", "normal");
    doc.setFontSize(9);
    setText(COLORS.inkSoft);
    const lines = it.brief.split("\n");
    lines.forEach((ln, idx) => doc.text(ln, textX, iy + 11 + idx * 5));
    iy += Math.max(it.h, lines.length * 5 + 12) + 6;
  }

  drawPageChrome(5, TOTAL_PAGES);

  // ============ PAGE 6 — BANNERS ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("05  ·  BANNERS & PLATFORM SIZES", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(28);
  setText(COLORS.ink);
  doc.text("Sized for every shop, feed & channel.", M, 58);

  const banners = [
    ["Etsy shop cover", "3360 × 840 px", "Etsy POD storefront"],
    ["Shopify hero", "1920 × 1080 px", "Website homepage banner"],
    ["YouTube cover", "2560 × 1440 px", "Channel art"],
    ["YouTube thumbnail", "1280 × 720 px", "Per-video thumbnail template"],
    ["Facebook cover", "1640 × 924 px", "Business page header"],
    ["Instagram post", "1080 × 1080 px", "Square feed post"],
    ["Instagram story", "1080 × 1920 px", "Vertical story / reel cover"],
  ];
  let by = 74;
  setStroke(COLORS.gold);
  doc.setLineWidth(0.15);
  doc.line(M, by, W - M, by);
  by += 8;
  banners.forEach((b) => {
    doc.setFont("times", "italic");
    doc.setFontSize(13);
    setText(COLORS.ink);
    doc.text(b[0], M, by);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    setText(COLORS.goldDeep);
    doc.text(b[1], M + 72, by);
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    setText(COLORS.inkSoft);
    doc.text(b[2], M + 112, by);
    by += 10;
    setStroke(COLORS.gold);
    doc.setLineWidth(0.08);
    doc.line(M, by - 3, W - M, by - 3);
  });

  by += 6;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("USAGE CONTEXT", M, by, { charSpace: 2 });
  by += 8;
  doc.setFont("times", "normal");
  doc.setFontSize(10.5);
  setText(COLORS.ink);
  const usage = [
    "Print on Demand (Printful, Printify, Gelato) — clothing, mugs, tote bags, hoodies.",
    "Etsy storefront and listing images.",
    "Shopify website headers and product packaging.",
    "Embroidery — the RW signature must convert cleanly for a digitizer.",
    "Social media — Instagram, Facebook, YouTube, Pinterest.",
  ];
  usage.forEach((u) => { doc.text("·  " + u, M, by); by += 6.5; });

  drawPageChrome(6, TOTAL_PAGES);

  // ============ PAGE 7 — DELIVERABLES ============
  doc.addPage();
  setFill(COLORS.creamSoft);
  doc.rect(0, 0, W, H, "F");

  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("06  ·  DELIVERABLES & NEXT STEPS", M, 40, { charSpace: 2 });

  doc.setFont("times", "italic");
  doc.setFontSize(28);
  setText(COLORS.ink);
  doc.text("What we'd love from you.", M, 58);

  const deliv = [
    ["Master files", "Adobe Illustrator (.ai) + EPS + PDF for every logo variation."],
    ["Export suite", "PNG (transparent) at 2000 × 2000 px and 4500 × 5400 px."],
    ["Refined main logo", "More organic tree texture; softer hand-drawn feel."],
    ["Hand-lettered RW signature", "Genuine calligraphy, embroidery-ready SPX."],
    ["Pillar symbol", "Slightly more botanical; flower less geometric."],
    ["Lock-up sheet", "Full · simplified · horizontal · vertical · monochrome variants."],
    ["Clear-space & min-size rules", "One page reference for future consistency."],
  ];
  let dy = 78;
  deliv.forEach((d) => {
    setFill(COLORS.gold);
    doc.circle(M + 1.5, dy - 1.5, 1, "F");
    doc.setFont("times", "italic");
    doc.setFontSize(12);
    setText(COLORS.ink);
    doc.text(d[0], M + 6, dy);
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    setText(COLORS.inkSoft);
    doc.text(d[1], M + 62, dy);
    dy += 10;
  });

  // Assets already provided
  dy += 6;
  setStroke(COLORS.gold);
  doc.setLineWidth(0.2);
  doc.line(M, dy, M + 30, dy);
  dy += 6;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("ALREADY PROVIDED", M, dy, { charSpace: 2 });
  dy += 6;
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  setText(COLORS.ink);
  const provided = [
    "5 first-draft SVG logos (Main · Rona Walk · Micro · Signature · Pillar).",
    "7 banners (Etsy · Shopify · YouTube cover + thumbnail · Facebook · IG post + story).",
    "Complete .zip brand kit with multi-size PNG exports.",
    "Live brand hub URL (share on request).",
  ];
  provided.forEach((p) => { doc.text("·  " + p, M, dy); dy += 5.8; });

  // Timeline & budget placeholder
  dy += 8;
  setStroke(COLORS.gold);
  doc.setLineWidth(0.2);
  doc.line(M, dy, M + 30, dy);
  dy += 6;
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  setText(COLORS.goldDeep);
  doc.text("TIMELINE · BUDGET · CONTACT", M, dy, { charSpace: 2 });
  dy += 8;
  const fields = [
    ["Timeline", CONTACT.timeline],
    ["Budget",   CONTACT.budget],
    ["From",     CONTACT.from],
    ["Email",    CONTACT.email],
  ];
  fields.forEach((f) => {
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    setText(COLORS.ink);
    doc.text(f[0], M, dy);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    setText(COLORS.goldDeep);
    doc.text(f[1], M + 26, dy);
    dy += 9;
  });

  // closing
  dy += 6;
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  setText(COLORS.goldDeep);
  doc.text("Thank you for helping bring Rona Wellness to life.", M, dy);

  drawPageChrome(7, TOTAL_PAGES);

  // ---------- save ----------
  const blob = doc.output("blob");
  saveAs(blob, "rona-wellness-designer-brief.pdf");
}
